import type { Block, BlogPost, Inline } from './types';
import { thePriceOfStandingStill } from './posts/the-price-of-standing-still';

export type { Block, BlogPost, Inline, Run } from './types';

/** Canonical origin. Used for canonical URLs, Open Graph and the sitemap. */
export const SITE_URL = 'https://orbiswisdomx.com';

export const BLOG_PATH = '/blog/';

/**
 * Every published post, newest first.
 *
 * To add a post: create posts/<slug>.ts, add it here, add the HTML shell
 * landing/blog/<slug>/index.html and the 1200×630 social image under
 * landing/public/og/. The build fails if a post and its shell don't match.
 * Full steps: landing/README.md → "Blog".
 */
export const POSTS: readonly BlogPost[] = [thePriceOfStandingStill].sort((a, b) =>
  b.publishedAt.localeCompare(a.publishedAt),
);

export function postPath(post: Pick<BlogPost, 'slug'>): string {
  return `${BLOG_PATH}${post.slug}/`;
}

export function findPost(slug: string): BlogPost | undefined {
  return POSTS.find((p) => p.slug === slug);
}

function inlineText(text: Inline): string {
  return typeof text === 'string' ? text : text.map((r) => r.v).join('');
}

function blockText(block: Block): string {
  switch (block.kind) {
    case 'p':
      return inlineText(block.text);
    case 'h2':
    case 'h3':
    case 'quote':
      return block.text;
    case 'voices':
      return block.items.join(' ');
    case 'stats':
      return block.items.map((i) => `${i.value} ${i.label}`).join(' ');
    case 'table':
      return [block.caption, ...block.head, ...block.rows.flat()].join(' ');
    case 'list':
      return block.items.map(inlineText).join(' ');
    case 'flow':
      return block.stages.map((s) => `${s.title} ${s.body}`).join(' ');
    case 'cards':
    case 'steps':
      return block.items.map((i) => `${i.title} ${i.body}`).join(' ');
  }
}

/** Estimated reading time at ~220 words per minute, minimum 1. */
export function readingMinutes(post: BlogPost): number {
  const words = post.blocks.map(blockText).join(' ').split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}

/** "6 October 2026" — en-GB, matching the company's UK registration. */
export function formatPostDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
